/* ============================================
   KONOOZ STORE LOCATOR — store-locator.js
   ES6+ | No jQuery | Leaflet + OpenStreetMap
   ============================================ */

(function () {
  'use strict';

  /* ── Helpers ─────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  function debounce(fn, ms) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  }

  function haversine(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function showToast(msg) {
    let toast = $('#sl-toast-global');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'sl-toast-global';
      toast.className = 'sl-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('show');
    setTimeout(() => toast.classList.remove('show'), 2800);
  }

  /* ── Gold SVG Marker ─────────────────────── */
  function createGoldIcon() {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.3)"/>
          </filter>
        </defs>
        <path d="M16 0C7.163 0 0 7.163 0 16c0 10 16 26 16 26S32 26 32 16C32 7.163 24.837 0 16 0z"
          fill="#B79A57" filter="url(#shadow)"/>
        <circle cx="16" cy="16" r="7" fill="white" opacity="0.9"/>
        <circle cx="16" cy="16" r="4" fill="#B79A57"/>
      </svg>`;
    return L.divIcon({
      html: svg,
      className: 'sl-marker-icon',
      iconSize: [32, 42],
      iconAnchor: [16, 42],
      popupAnchor: [0, -44]
    });
  }

  /* ── Popup HTML ──────────────────────────── */
  function buildPopupHTML(store) {
    const imgHTML = store.image
      ? `<img src="${store.image}" alt="${store.name}" class="sl-popup-img" loading="lazy"/>`
      : `<div class="sl-popup-img-placeholder">
           <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
             fill="none" stroke="#B79A57" stroke-width="1.5">
             <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
             <polyline points="9 22 9 12 15 12 15 22"/>
           </svg>
         </div>`;
    const dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;
    return `
      <div class="sl-popup">
        ${imgHTML}
        <div class="sl-popup-body">
          <div class="sl-popup-name">${store.name}</div>
          ${store.code ? `<div class="sl-popup-code">${store.code}</div>` : ''}
          ${store.addressEn ? `<div class="sl-popup-address">${store.addressEn}</div>` : ''}
          ${store.hours ? `<div class="sl-popup-address" style="font-size:11px;">⏰ ${store.hours}</div>` : ''}
          <a href="${dirUrl}" target="_blank" rel="noopener noreferrer" class="sl-popup-dir-btn">
            ➤ Get Directions
          </a>
        </div>
      </div>`;
  }

  /* ── Main Init ───────────────────────────── */
  function initStoreLocator(sectionId) {
    const sId = sectionId;

    /* --- Parse data --- */
    const dataEl = $(`#store-locator-data-${sId}`);
    if (!dataEl) return;
    let stores = [];
    try { stores = JSON.parse(dataEl.textContent); } catch (e) { console.warn('Store Locator: invalid JSON', e); return; }
    if (!stores.length) return;

    /* --- DOM refs --- */
    const section    = $(`#store-locator-${sId}`);
    const searchEl   = $(`#sl-search-${sId}`);
    const countryEl  = $(`#sl-country-${sId}`);
    const cityEl     = $(`#sl-city-${sId}`);
    const locateBtn  = $(`#sl-locate-${sId}`);
    const listEl     = $(`#sl-list-${sId}`);
    const emptyEl    = $(`#sl-empty-${sId}`);
    const resultsEl  = $(`#sl-results-count-${sId}`);
    const mapEl      = $(`#store-map-${sId}`);
    const cards      = $$('.sl-card', listEl);

    /* --- Favourites from localStorage --- */
    const FAV_KEY = `sl-favs-${sId}`;
    let favs = JSON.parse(localStorage.getItem(FAV_KEY) || '[]');

    function saveFavs() { localStorage.setItem(FAV_KEY, JSON.stringify(favs)); }

    /* Apply saved favs on load */
    cards.forEach(card => {
      const idx = parseInt(card.dataset.index, 10);
      const btn = $('.sl-fav-btn', card);
      if (favs.includes(idx)) btn.classList.add('active');
    });

    /* --- Populate filter dropdowns --- */
    if (countryEl && cityEl) {
      const countries = [...new Set(stores.map(s => s.country).filter(Boolean))].sort();
      const allCities = [...new Set(stores.map(s => s.city).filter(Boolean))].sort();

      countries.forEach(c => {
        const opt = document.createElement('option');
        opt.value = c; opt.textContent = c;
        countryEl.appendChild(opt);
      });

      function populateCities(filterCountry = '') {
        cityEl.innerHTML = '<option value="">All Cities</option>';
        const cities = filterCountry
          ? [...new Set(stores.filter(s => s.country === filterCountry).map(s => s.city).filter(Boolean))].sort()
          : allCities;
        cities.forEach(c => {
          const opt = document.createElement('option');
          opt.value = c; opt.textContent = c;
          cityEl.appendChild(opt);
        });
      }

      populateCities();

      countryEl.addEventListener('change', () => {
        populateCities(countryEl.value);
        applyFilters();
      });

      cityEl.addEventListener('change', applyFilters);
    }

    /* --- Map init (lazy via IntersectionObserver) --- */
    let map, clusterGroup, markers = [];
    let mapInitialized = false;

    function initMap() {
      if (mapInitialized || !mapEl) return;
      mapInitialized = true;

      map = L.map(mapEl, {
        zoomControl: true,
        scrollWheelZoom: false,
        attributionControl: true
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19
      }).addTo(map);

      clusterGroup = L.markerClusterGroup({
        maxClusterRadius: 60,
        showCoverageOnHover: false
      });

      const goldIcon = createGoldIcon();
      const bounds = [];

      stores.forEach((store, i) => {
        const lat = parseFloat(store.lat);
        const lng = parseFloat(store.lng);
        if (!lat || !lng) return;

        const marker = L.marker([lat, lng], { icon: goldIcon, title: store.name });
        marker.bindPopup(buildPopupHTML(store), { maxWidth: 260, minWidth: 220 });

        marker.on('click', () => {
          highlightCard(i);
          bounceMarker(marker);
        });

        markers[i] = marker;
        clusterGroup.addLayer(marker);
        bounds.push([lat, lng]);
      });

      map.addLayer(clusterGroup);

      if (bounds.length > 1) {
        map.fitBounds(bounds, { padding: [40, 40] });
      } else if (bounds.length === 1) {
        map.setView(bounds[0], 14);
      }
    }

    /* Lazy load map */
    if ('IntersectionObserver' in window && mapEl) {
      const obs = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) { initMap(); obs.disconnect(); }
      }, { threshold: 0.1 });
      obs.observe(mapEl);
    } else {
      initMap();
    }

    /* --- Marker bounce --- */
    function bounceMarker(marker) {
      const el = marker.getElement();
      if (!el) return;
      el.classList.remove('sl-marker-bounce');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => el.classList.add('sl-marker-bounce'));
      });
      el.addEventListener('animationend', () => el.classList.remove('sl-marker-bounce'), { once: true });
    }

    /* --- Highlight card + zoom map --- */
    function highlightCard(index) {
      cards.forEach(c => c.classList.remove('active'));
      const card = cards[index];
      if (!card) return;
      card.classList.add('active');
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

      const store = stores[index];
      if (!store || !map) return;
      const lat = parseFloat(store.lat);
      const lng = parseFloat(store.lng);
      if (!lat || !lng) return;

      map.flyTo([lat, lng], 15, { duration: 0.8 });

      const marker = markers[index];
      if (marker) {
        clusterGroup.zoomToShowLayer(marker, () => {
          marker.openPopup();
          bounceMarker(marker);
        });
      }
    }

    /* --- Card click & keyboard --- */
    cards.forEach(card => {
      card.addEventListener('click', () => highlightCard(parseInt(card.dataset.index, 10)));
      card.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          highlightCard(parseInt(card.dataset.index, 10));
        }
      });

      /* Favourite button */
      const favBtn = $('.sl-fav-btn', card);
      if (favBtn) {
        favBtn.addEventListener('click', e => {
          e.stopPropagation();
          const idx = parseInt(favBtn.dataset.index, 10);
          if (favs.includes(idx)) {
            favs = favs.filter(f => f !== idx);
            favBtn.classList.remove('active');
            favBtn.setAttribute('aria-label', 'Add to favourites');
          } else {
            favs.push(idx);
            favBtn.classList.add('active');
            favBtn.setAttribute('aria-label', 'Remove from favourites');
          }
          saveFavs();
        });
      }

      /* Copy address button */
      const copyBtn = $('.sl-copy-btn', card);
      if (copyBtn) {
        copyBtn.addEventListener('click', e => {
          e.stopPropagation();
          const addr = copyBtn.dataset.address;
          if (navigator.clipboard) {
            navigator.clipboard.writeText(addr).then(() => showToast('Address copied!'));
          } else {
            const ta = document.createElement('textarea');
            ta.value = addr;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            showToast('Address copied!');
          }
        });
      }
    });

    /* --- Filter logic --- */
    function applyFilters() {
      const query  = searchEl ? searchEl.value.trim().toLowerCase() : '';
      const country = countryEl ? countryEl.value : '';
      const city    = cityEl ? cityEl.value : '';

      let visibleCount = 0;

      cards.forEach(card => {
        const s = stores[parseInt(card.dataset.index, 10)];
        if (!s) return;

        const searchable = [
          s.name, s.code, s.addressEn, s.addressAr,
          s.city, s.country, s.phone
        ].join(' ').toLowerCase();

        const matchSearch  = !query   || searchable.includes(query);
        const matchCountry = !country || s.country === country;
        const matchCity    = !city    || s.city === city;
        const visible      = matchSearch && matchCountry && matchCity;

        card.classList.toggle('hidden', !visible);

        /* Show/hide map marker */
        if (map && markers[s.index] !== undefined) {
          if (visible) {
            if (!clusterGroup.hasLayer(markers[s.index])) clusterGroup.addLayer(markers[s.index]);
          } else {
            clusterGroup.removeLayer(markers[s.index]);
          }
        }

        if (visible) visibleCount++;
      });

      if (emptyEl) emptyEl.hidden = visibleCount > 0;
      if (resultsEl) resultsEl.textContent = `${visibleCount} store${visibleCount !== 1 ? 's' : ''} found`;
    }

    /* --- Search --- */
    if (searchEl) {
      searchEl.addEventListener('input', debounce(applyFilters, 300));
    }

    /* --- Locate Me --- */
    if (locateBtn) {
      locateBtn.addEventListener('click', () => {
        if (!navigator.geolocation) { showToast('Geolocation not supported.'); return; }
        locateBtn.textContent = 'Locating…';
        locateBtn.disabled = true;

        navigator.geolocation.getCurrentPosition(pos => {
          const { latitude: uLat, longitude: uLng } = pos.coords;
          let nearest = null, minDist = Infinity;

          stores.forEach((s, i) => {
            const lat = parseFloat(s.lat), lng = parseFloat(s.lng);
            if (!lat || !lng) return;
            const d = haversine(uLat, uLng, lat, lng);
            if (d < minDist) { minDist = d; nearest = i; }
          });

          locateBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="width:16px;height:16px"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg> Locate Me`;
          locateBtn.disabled = false;

          if (nearest !== null) {
            highlightCard(nearest);
            showToast(`Nearest store: ${stores[nearest].name} (${minDist.toFixed(1)} km away)`);
          }
        }, () => {
          locateBtn.innerHTML = 'Locate Me';
          locateBtn.disabled = false;
          showToast('Could not get your location.');
        });
      });
    }
  }

  /* ── Bootstrap ───────────────────────────── */
  function boot() {
    const sId = window.storeLocatorSectionId;
    if (sId) {
      initStoreLocator(sId);
    } else {
      /* Fallback: find any store locator section */
      $$('[id^="store-locator-"]').forEach(el => {
        if (!el.id) return;
        const id = el.id.replace('store-locator-', '');
        initStoreLocator(id);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
